import {ChangeDetectorRef, Component, OnDestroy, OnInit, Optional} from "@angular/core";
import {AuthApiService} from "../../auth/auth-api.service";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {ErrorHandlerService} from "../../../error-handler.service";
import {FUISpinnersService, FUIStackedPageContextService, FUIStackedPageService} from "@fedelta-pos/ui";
import {ActivatedRoute, Router} from "@angular/router";
import {CustomerInstanceGroupData, CustomerInstanceGroupSalesData} from "../customer-instance-group.data";
import {CustomerInstanceGroupService} from "../customer-instance-group.service";
import {CustomerInstanceData} from "../../customer-instance/customer-instance.data";
import {SubscriptionData} from "../../customer-instance-venue/venue-plan/subscription.data";
import {CustomerInstanceSearchPageComponent} from "../../customer-instance/customer-instance-search/customer-instance-search-page.component";
import {BehaviorSubject, Subscription} from "rxjs";

@Component({
    selector: "fhm-customer-instance-group-edit-page",
    styles: [":host {display: flex; width: 100%;}"],
    templateUrl: "./customer-instance-group-edit-page.component.html",
    providers: [CustomerInstanceGroupService],
    standalone: false
})
export class CustomerInstanceGroupEditPageComponent implements OnDestroy, OnInit {

    id: string;
    data: CustomerInstanceGroupData = new CustomerInstanceGroupData();

    instances: CustomerInstanceData[] = [];

    subscriptionData: SubscriptionData;

    groupSales: CustomerInstanceGroupSalesData;

    newCustomerInstanceGroup: boolean;

    formGroup: FormGroup;

    editingInstance: boolean;

    private subscriptions: Subscription = new Subscription();

    constructor(
        private customerInstanceGroupService: CustomerInstanceGroupService,
        private auth: AuthApiService,
        private errorHandler: ErrorHandlerService,
        private spinnersService: FUISpinnersService,
        private changeRef: ChangeDetectorRef,
        private router: Router,
        private route: ActivatedRoute,
        private formBuilder: FormBuilder,
        private stackedPageService: FUIStackedPageService,
        @Optional() private stackedPageContextService: FUIStackedPageContextService) {

        this.formGroup = this.formBuilder.group({
            groupId: ["", [Validators.required, Validators.maxLength(35)]]
        });

        this.formGroup.get("groupId")["required$"] = new BehaviorSubject(true);
    }

    ngOnInit(): void {
        this.route.paramMap.subscribe(async (params) => {
            this.id = params.get("id");
            this.init();
        });
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    async init(): Promise<void> {
        if (this.id === "new") {
            this.data = new CustomerInstanceGroupData();
            this.newCustomerInstanceGroup = true;
        } else {
            this.spinnersService.open();
            this.newCustomerInstanceGroup = false;
            try {
                this.data = await this.customerInstanceGroupService.getCustomerInstanceGroup(this.id);
                this.instances = await this.customerInstanceGroupService.getCustomerInstances(this.id);
                this.subscriptionData = await this.customerInstanceGroupService.getCustomerInstanceGroupSubscription(this.id);
                this.groupSales = await this.customerInstanceGroupService.getCustomerInstanceGroupSales(this.id);
            } catch (error) {
                this.errorHandler.handleErrorResponse(error);
            } finally {
                this.spinnersService.close();
            }
        }
    }

    get title(): string {
        if (this.newCustomerInstanceGroup) {
            return "New Group";
        }

        if (!this.data) {
            return "";
        }

        if (!this.data.id) {
            return "New Group";
        }

        return this.data.groupId;
    }


    done(): void {
        this.router.navigate(["/main/customer-instance-group-search"]);
    }

    editInstance(row: any): void {
        this.router.navigate(["/main/customer-instance-edit/" + row["id"]]);
    }

    addExistingInstance(): void {
        this.stackedPageService.open(CustomerInstanceSearchPageComponent, {searchMode: true}).toPromise().then(async (data) => {
            if (data) {
                try {
                    await this.customerInstanceGroupService.assignInstanceGroupToCustomerInstance(data, this.id);
                } catch (e) {
                    this.errorHandler.handleErrorResponse(e);
                }
                this.instances = await this.customerInstanceGroupService.getCustomerInstances(this.id);
            }
        });
    }

    async save(): Promise<void> {
        Object.keys((this.formGroup.controls)).forEach((key) => {
            this.formGroup.controls[key].markAsTouched();
            this.formGroup.controls[key].updateValueAndValidity();
        });
        if (this.formGroup.valid) {
            this.spinnersService.open();
            try {
                this.data = await this.customerInstanceGroupService.createCustomerInstanceGroup(this.data);
                this.editingInstance = false;
                this.id = this.data.id;
                await this.router.navigate(["/main/customer-instance-group-edit/" + this.data.id]);
                this.init();
            } catch (error) {
                this.errorHandler.handleErrorResponse(error);
            } finally {
                this.spinnersService.close();
            }
        }
    }

}
